import { toRosterMemberKey } from '@sorrem-client/ui/shared/roster-member-key';
import type { CrucibleRosterEntry, PlayerStatus } from '@sorrem/shared';
import type { UIPartyEntry } from '@sorrem-client/types';
import { isDown } from '@sorrem/shared';

/** Below this share of their essence, a member's row is marked Low. */
export const LOW_ESSENCE_RATIO = 0.25;

/** What a party panel row is marked with: in a run, what's wrong (none while all is well); at the gathering, readiness. */
export type PartyPanelMarker =
    | 'low'
    | Exclude<PlayerStatus, 'normal'>
    | 'fallen'
    | 'disconnected'
    | 'joining'
    | 'left'
    /* At the gathering: whether the member has readied up. */
    | 'ready'
    | 'not-ready';

export interface PartyPanelRow {
    /** The member's roster key: account and character. */
    readonly key: string;
    readonly name: string;
    readonly essence: number;
    readonly maxEssence: number;
    readonly marker: PartyPanelMarker | null;
    /** A fallen member's seconds left to choose, when known. */
    readonly fallenSecondsLeft: number | null;
    /** The member leads the party (created the crucible): a crown before their name. */
    readonly isLeader: boolean;
}

function resolveMarker(rosterEntry: CrucibleRosterEntry, member: UIPartyEntry | undefined): PartyPanelMarker | null {
    if (member === undefined) {
        // On the roster but not in the world: still arriving, or their place is held.
        return rosterEntry.connectionStatus === 'waiting-to-connect' ? 'disconnected' : 'joining';
    }
    if (member.isDisconnected) {
        return 'disconnected';
    }
    if (isDown(member)) {
        return 'fallen';
    }
    if (member.status !== 'normal') {
        return member.status;
    }
    return member.maxEssence > 0 && member.essence / member.maxEssence < LOW_ESSENCE_RATIO ? 'low' : null;
}

/**
 * The party panel's rows (#199): every other member on the run's roster, in
 * roster order, with their essence and what's wrong, if anything. Members
 * who left are dropped from the roster by the host; the panel shows them
 * as Left for a moment itself. The leader is known by account, when the
 * run was entered from the gathering.
 */
export function resolvePartyPanelRows(
    roster: readonly CrucibleRosterEntry[],
    party: readonly UIPartyEntry[],
    nowMs: number,
    leaderAccountId: string | null = null,
): PartyPanelRow[] {
    return roster
        .filter((rosterEntry) => !rosterEntry.isLocal)
        .map((rosterEntry) => {
            const member = party.find((entry) => !entry.isLocal && entry.id === rosterEntry.playerId);
            const marker = resolveMarker(rosterEntry, member);
            const deadlineMs = member?.fallenDecisionDeadlineMs ?? null;
            return {
                key: toRosterMemberKey(rosterEntry),
                name: rosterEntry.characterName || rosterEntry.playerName,
                essence: Math.max(0, member?.essence ?? 0),
                maxEssence: member?.maxEssence ?? 0,
                marker,
                fallenSecondsLeft: marker === 'fallen' && deadlineMs !== null
                    ? Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000))
                    : null,
                isLeader: rosterEntry.accountId === leaderAccountId,
            };
        });
}

/** Whether anyone besides the local player is on the run's roster: a solo run has no party to show (#294). */
export function hasOtherRosterMembers(roster: readonly CrucibleRosterEntry[]): boolean {
    return roster.some((rosterEntry) => !rosterEntry.isLocal);
}

/** A fall's countdown as minutes and seconds, e.g. "0:24". */
export function formatFallenCountdown(secondsLeft: number): string {
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
